/** Explicit operator adoption of current server-held imported listing metadata. */
import type { EBAY_SPORT_CARD_FEATURES, EbaySellerHubSinglesCategory } from './ebay-seller-hub';
import type { CardPresentation } from './card-presentation';
import type { CardIdentity } from './types';

export const IMPORTED_LISTING_REQUEST_MAX_CARDS = 10;
export const IMPORTED_LISTING_UI_MAX_CARDS = 50;
export const IMPORTED_LISTING_PREVIEW_TTL_SECONDS = 300;
/** Explicit client capability; omission keeps legacy preview/admission behavior. */
export const IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION = 2;
/** Explicit, separately opted-in condition adoption; version2 remains unchanged. */
export const IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION = 3;

/** Listing-draft snapshot only; it never grants or changes Card identity authority. */
export interface ImportedListingContextSnapshot {
  identitySource: 'CHECKLIST';
  checklistId: string;
  /** Full compared identity, including optional/removed values in before/after. */
  identity: CardIdentity;
}

export interface ImportedListingPrice {
  amountMinor: number;
  currency: 'USD';
}

export type ImportedListingChange =
  | {
      /** Version3 + includePresentation only; displays both independently saved preimages. */
      field: 'presentation';
      before: { card: CardPresentation | null; listing: CardPresentation | null };
      after: CardPresentation;
    }
  | {
      /** Version2 or later; explicit same-checklist draft rebase, never a Card identity edit. */
      field: 'listingContext';
      before: ImportedListingContextSnapshot;
      after: ImportedListingContextSnapshot;
    }
  | {
      /** Version2 or later; supported per-draft category, never a catalog default write. */
      field: 'listingCategory';
      before: EbaySellerHubSinglesCategory | null;
      after: EbaySellerHubSinglesCategory;
    }
  | { field: 'title' | 'description'; before: string | null; after: string }
  | { field: 'askingPrice'; before: ImportedListingPrice | null; after: ImportedListingPrice }
  | {
      field: 'features';
      before: (typeof EBAY_SPORT_CARD_FEATURES)[number][] | null;
      after: (typeof EBAY_SPORT_CARD_FEATURES)[number][];
    };

export type ImportedListingPreviewRequest = {
  operation: 'PREVIEW_IMPORTED_LISTING';
  batchId: string;
  /** Explicit selection: unique IDs, 1–10 per request. */
  cardIds: string[];
} & (
  | {
      previewVersion?: typeof IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION;
      includePresentation?: never;
    }
  | {
      previewVersion: typeof IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION;
      includePresentation?: true;
    }
);

/** Captured first-field comparison only. Read-only guidance, never an Apply input. */
type ImportedListingIdentityValue<T extends string | boolean | number> =
  | Readonly<{ state: 'missing' }>
  | Readonly<{ state: 'value'; value: T }>;
export type ImportedListingIdentityMismatch = Readonly<{ code: 'SOURCE_IDENTITY_MISMATCH' }> &
  (
    | Readonly<{
        field:
          | 'category'
          | 'set'
          | 'number'
          | 'name'
          | 'year'
          | 'brand'
          | 'subset'
          | 'attributes'
          | 'team'
          | 'language'
          | 'variation';
        confirmedCard: ImportedListingIdentityValue<string>;
        currentChecklist:
          | ImportedListingIdentityValue<string>
          | Readonly<{ state: 'REMOVED_BY_CORRECTION' }>;
      }>
    | Readonly<{
        field: 'rookie' | 'autograph' | 'memorabilia' | 'serialNumbered';
        confirmedCard: ImportedListingIdentityValue<boolean>;
        currentChecklist:
          | ImportedListingIdentityValue<boolean>
          | Readonly<{ state: 'REMOVED_BY_CORRECTION' }>;
      }>
    | Readonly<{
        field: 'serialTo';
        confirmedCard: ImportedListingIdentityValue<number>;
        currentChecklist:
          | ImportedListingIdentityValue<number>
          | Readonly<{ state: 'REMOVED_BY_CORRECTION' }>;
      }>
  );

export interface ImportedListingPreviewResult {
  cardId: string;
  status: 'READY' | 'UNCHANGED' | 'BLOCKED';
  changes: ImportedListingChange[];
  reasons: string[];
  /** Optional bounded explanation of a BLOCKED row; no identity or listing authority. */
  identityMismatch?: ImportedListingIdentityMismatch;
  /** RFC-0005 P1.2: the reviewed listing replaces a TCVS-made, unpublished draft (reviewed-apply only). */
  replacesDraft?: true;
}

export type ImportedListingPreviewResponse = {
  /** Opaque, persisted actor/batch/source/state binding; never a client-authority token. */
  previewId: string;
  expiresAt: string;
  results: ImportedListingPreviewResult[];
} & (
  | {
      previewVersion?: typeof IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION;
      includePresentation?: never;
    }
  | {
      previewVersion: typeof IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION;
      /** Required resolved scope echo for version3, including false when omitted in the request. */
      includePresentation: boolean;
    }
);

export interface ImportedListingApplyRequest {
  operation: 'APPLY_IMPORTED_LISTING';
  batchId: string;
  previewId: string;
  /** Unique subset of this preview's READY cards; no client-supplied listing values. */
  cardIds: string[];
  /** Explicit confirmation of all displayed changes; capability comes only from stored preview. */
  attested: true;
}

export interface ImportedListingApplyResult {
  cardId: string;
  status: 'APPLIED' | 'UNCHANGED' | 'CONFLICT' | 'BLOCKED' | 'FAILED' | 'UNKNOWN';
  reasons: string[];
  /** Durable per-card receipt when a committed outcome is known. */
  receiptId?: string;
}

export interface ImportedListingApplyResponse {
  previewId: string;
  results: ImportedListingApplyResult[];
}
